import * as React from "react";
import { GoogleAnalytics } from "@grandvisionhq/analytics";
import { Context } from "@grandvisionhq/carts-v2";
import { Util } from "@grandvisionhq/common";
import { useLink } from "@grandvisionhq/frk-routing";
import { usePaymentsUIContext, PaymentFlow } from "@grandvisionhq/payments";
import { useIntl } from "@grandvisionhq/state";
import { useCreateOrder } from "../../../../hooks/orders/hooks/orders/use-create-order.js";
import { useRedirectToOrderComplete } from "../../../../hooks/orders/hooks/orders/use-redirect-to-order-confirm-page.js";
import { mapPaymentDetailsForOrder } from "../../../../mappers/data/mappers/data/payment-details-mapper.js";
import { CreateOrderErrors } from "../../../../mappers/mappers/errors-mapping.js";
import { mapConsentsToOrderModel } from "../../../../utils/utils/map-consent-to-order-model.js";
const useHandleOrderCreation = () => {
  const { cart } = Context.useCart();
  const { href: returnPath } = useLink({
    route: "express.checkout.login",
    params: {
      cartId: "",
      cartHash: ""
    }
  }) || {};
  const intl = useIntl();
  const { isQrCodeModalOpen } = usePaymentsUIContext();
  const prevIsQrCodeModalOpenValue = Util.usePrevious(isQrCodeModalOpen);
  const [createOrder, { orderResponse, paymentFlow, setPaymentFlow }] = useCreateOrder({
    returnPath
  });
  React.useEffect(() => {
    if (prevIsQrCodeModalOpenValue !== isQrCodeModalOpen) {
      if (cart?.id && !isQrCodeModalOpen) {
        setPaymentFlow(PaymentFlow.UNDEFINED);
      }
    }
  }, [cart?.id, isQrCodeModalOpen, prevIsQrCodeModalOpenValue]);
  const redirectToOrderComplete = useRedirectToOrderComplete();
  const handlePlaceOrder = React.useCallback(
    async ({
      selectedPaymentMethod,
      selfUrl,
      consents,
      paymentDetails,
      donationInfo,
      isSaveDetailsInAccount = false,
      setIsOrderInProgress
    }) => {
      if (!cart || !cart.id || !selectedPaymentMethod) {
        console.error("No Cart");
        throw CreateOrderErrors.ORDER_CREATION_CALL_FAILED;
      }
      const analyticsId = await GoogleAnalytics.getAnalyticsId().then((id) => id);
      const mappedConsents = consents && mapConsentsToOrderModel(consents);
      const orderDraft = mapPaymentDetailsForOrder({
        intl,
        cartId: cart.id,
        totalPrice: cart.prices.totalPrice,
        returnUrl: `${selfUrl}/api/payment-callback?type=${selectedPaymentMethod?.type}&checkoutType=logged-in-regular`,
        paymentDetails,
        selectedPaymentMethod
      });
      const placeOrderInput = {
        ...orderDraft,
        ...analyticsId ? { analyticsId } : null,
        ...mappedConsents ? { consents: mappedConsents } : null,
        ...donationInfo && donationInfo.amount ? {
          donationIntent: {
            amount: {
              centAmount: donationInfo.amount,
              currencyCode: intl.currency
            },
            foundationKey: donationInfo.key
          }
        } : null,
        shouldCreateAccount: false,
        overwritePersonalDetails: isSaveDetailsInAccount
      };
      const createOrderResult = await createOrder(placeOrderInput);
      if (createOrderResult && createOrderResult.success && !createOrderResult.redirect && createOrderResult.resultType !== "QRCODE") {
        try {
          redirectToOrderComplete({
            cartId: createOrderResult?.cartId,
            orderData: createOrderResult?.orderDataToken,
            checkoutMethod: "login_checkout"
          });
        } catch (error) {
          console.error(error);
          setIsOrderInProgress(false);
        }
      } else {
        setIsOrderInProgress(false);
      }
    },
    [cart, createOrder, intl, redirectToOrderComplete]
  );
  return {
    orderResponse,
    paymentFlow,
    handlePlaceOrder
  };
};
export {
  useHandleOrderCreation
};
//# sourceMappingURL=useHandlerOrderCreation.js.map
